import mixpanel, { Config, Dict } from 'mixpanel-browser'
import {
  MixpanelConfig,
  UserProfile,
  TrackingProperties,
  EventName,
  EventCategory,
  TrackingEvent,
} from '@/types/analytics'

class AnalyticsService {
  private initialized = false
  private config: MixpanelConfig | null = null
  private userId: string | null = null
  private superProperties: Dict = {}

  private getEnvironment(): 'local' | 'staging' | 'production' {
    const appUrl = process.env.NEXT_PUBLIC_APP_URL || ''

    if (appUrl.includes('localhost')) {
      return 'local'
    } else if (appUrl.includes('demo.aloesuite.com')) {
      return 'staging'
    } else if (appUrl.includes('app.aloesuite.com')) {
      return 'production'
    }

    return 'local' // default fallback
  }

  private getEnvironmentConfig(): MixpanelConfig | null {
    const token = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN

    if (!token) {
      console.warn('Mixpanel: No token configured for current environment')
      return null
    }

    const env = this.getEnvironment()

    return {
      token,
      debug: env === 'local',
      // Disabled on purpose: Mixpanel's built-in pageview tracking monkey-patches
      // history.pushState/replaceState. On Chrome-for-iOS that wrapper collides
      // with the browser's own history wrapper (plus Clarity and Sentry) into a
      // recursion cycle that overflows the stack (Sentry ALOESUITE-PROD-BR).
      // Pageviews are fired manually from Next's router events in _app.tsx via
      // trackNativePageview(), which sends the same event without the patch.
      track_pageview: false,
      ignore_dnt: true,
      persistence: 'localStorage',
      api_host: 'https://api-eu.mixpanel.com',
    }
  }

  initialize(): void {
    if (this.initialized || typeof window === 'undefined') {
      return
    }

    const config = this.getEnvironmentConfig()

    if (!config) {
      console.warn('Mixpanel: No token configured for current environment')
      return
    }

    this.config = config

    const mixpanelConfig: Partial<Config> = {
      debug: config.debug,
      track_pageview: config.track_pageview,
      persistence: config.persistence,
      ignore_dnt: true,
      property_blacklist: ['$current_url', '$initial_referrer', '$referrer'],
      api_host: config.api_host,
    }

    try {
      mixpanel.init(config.token, mixpanelConfig)
      this.initialized = true

      // Set default super properties
      this.setSuperProperties({
        environment: this.getEnvironment(),
        platform: 'web',
        appVersion: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
      })

      if (config.debug) {
        console.log('Mixpanel initialized successfully')
      }
    } catch (error) {
      console.error('Failed to initialize Mixpanel:', error)
      this.initialized = false
    }
  }

  isInitialized(): boolean {
    return this.initialized
  }

  identify(userId: string, userProfile?: UserProfile): void {
    if (!this.initialized) {
      console.warn('Mixpanel not initialized')
      return
    }

    try {
      // Identify the user
      mixpanel.identify(userId)
      this.userId = userId

      // Set user profile properties
      if (userProfile) {
        const profileData: Dict = {
          $email: userProfile.email,
          $name: userProfile.name,
          $created: userProfile.createdAt,
          companyId: userProfile.companyId,
          companyName: userProfile.companyName,
          role: userProfile.role,
          plan: userProfile.plan,
        }

        // Remove undefined values
        Object.keys(profileData).forEach((key) => {
          if (profileData[key] === undefined) {
            delete profileData[key]
          }
        })

        mixpanel.people.set(profileData)

        // Also set some properties as super properties for all events
        this.setSuperProperties({
          userId,
          userEmail: userProfile.email,
          companyId: userProfile.companyId,
          companyName: userProfile.companyName,
          userRole: userProfile.role,
        })
      }
    } catch (error) {
      console.error('Failed to identify user:', error)
    }
  }

  reset(): void {
    if (!this.initialized) return

    try {
      mixpanel.reset()
      this.userId = null
      this.superProperties = {}

      // Re-set environment super properties after reset
      this.setSuperProperties({
        environment: this.getEnvironment(),
        platform: 'web',
        appVersion: process.env.NEXT_PUBLIC_APP_VERSION || '1.0.0',
      })
    } catch (error) {
      console.error('Failed to reset Mixpanel:', error)
    }
  }

  track(eventName: EventName | string, properties?: TrackingProperties): void {
    // Lazy init: child effects (e.g. Order Complete on the paid success page
    // after a Stripe redirect) run before _app.tsx's init useEffect, so the
    // first track() call on a fresh page load would otherwise be dropped.
    if (!this.initialized) {
      this.initialize()
    }

    if (!this.initialized) {
      console.warn('Mixpanel not initialized')
      return
    }

    try {
      const eventProperties = {
        ...properties,
        timestamp: new Date().toISOString(),
        sessionId: this.getSessionId(),
      }

      mixpanel.track(eventName, eventProperties)
    } catch (error) {
      console.error(`Failed to track event ${eventName}:`, error)
    }
  }

  trackEvent(event: TrackingEvent): void {
    if (!this.initialized) return

    const properties = {
      ...event.properties,
      category: event.category,
    }

    this.track(event.name, properties)
  }

  trackPageView(pageName: string, properties?: TrackingProperties): void {
    this.track(EventName.PageView, {
      page: pageName,
      url: window.location.href,
      path: window.location.pathname,
      search: window.location.search,
      hash: window.location.hash,
      title: document.title,
      referrer: document.referrer,
      ...properties,
    })
  }

  // Fires Mixpanel's native pageview event ($mp_web_page_view). Called from
  // Next's router events in _app.tsx instead of enabling Mixpanel's
  // `track_pageview` auto-tracking, which monkey-patches history.pushState and
  // causes a Chrome-for-iOS stack-overflow crash (Sentry ALOESUITE-PROD-BR).
  // Same event, without the history patch. See getEnvironmentConfig() for why.
  trackNativePageview(): void {
    // Lazy init to match track(): the first navigation may fire before the
    // init effect in _app.tsx has run.
    if (!this.initialized) {
      this.initialize()
    }

    if (!this.initialized) return

    try {
      mixpanel.track_pageview()
    } catch (error) {
      console.error('Failed to track pageview:', error)
    }
  }

  timeEvent(eventName: EventName | string): void {
    if (!this.initialized) return

    try {
      mixpanel.time_event(eventName)
    } catch (error) {
      console.error(`Failed to time event ${eventName}:`, error)
    }
  }

  setSuperProperties(properties: Dict): void {
    if (!this.initialized) return

    try {
      // Filter out undefined values
      const filteredProperties: Dict = {}
      Object.keys(properties).forEach((key) => {
        if (properties[key] !== undefined) {
          filteredProperties[key] = properties[key]
        }
      })

      mixpanel.register(filteredProperties)
      this.superProperties = { ...this.superProperties, ...filteredProperties }
    } catch (error) {
      console.error('Failed to set super properties:', error)
    }
  }

  incrementUserProperty(property: string, value: number = 1): void {
    if (!this.initialized) return

    try {
      mixpanel.people.increment(property, value)
    } catch (error) {
      console.error(`Failed to increment property ${property}:`, error)
    }
  }

  setUserProperty(property: string | Dict, value?: any): void {
    if (!this.initialized) return

    try {
      if (typeof property === 'string') {
        mixpanel.people.set({ [property]: value })
      } else {
        mixpanel.people.set(property)
      }
    } catch (error) {
      console.error('Failed to set user property:', error)
    }
  }

  trackRevenue(amount: number, properties?: TrackingProperties): void {
    if (!this.initialized) return

    try {
      mixpanel.people.track_charge(amount, properties)
    } catch (error) {
      console.error('Failed to track revenue:', error)
    }
  }

  private getSessionId(): string {
    // Get or create a session ID (expires after 30 minutes of inactivity)
    const sessionKey = 'analytics_session_id'
    const sessionTimeout = 30 * 60 * 1000 // 30 minutes

    const now = Date.now()
    const sessionData = localStorage.getItem(sessionKey)

    if (sessionData) {
      const { id, lastActivity } = JSON.parse(sessionData)
      if (now - lastActivity < sessionTimeout) {
        localStorage.setItem(
          sessionKey,
          JSON.stringify({ id, lastActivity: now })
        )
        return id
      }
    }

    // Create new session
    const newSessionId = `session_${now}_${Math.random()
      .toString(36)
      .substring(2, 9)}`
    localStorage.setItem(
      sessionKey,
      JSON.stringify({ id: newSessionId, lastActivity: now })
    )
    return newSessionId
  }

  getDistinctId(): string | undefined {
    if (!this.initialized) return undefined

    try {
      return mixpanel.get_distinct_id()
    } catch (error) {
      console.error('Failed to get distinct ID:', error)
      return undefined
    }
  }

  optOut(): void {
    if (!this.initialized) return

    try {
      mixpanel.opt_out_tracking()
    } catch (error) {
      console.error('Failed to opt out:', error)
    }
  }

  optIn(): void {
    if (!this.initialized) return

    try {
      mixpanel.opt_in_tracking()
    } catch (error) {
      console.error('Failed to opt in:', error)
    }
  }
}

export const analyticsService = new AnalyticsService()
export default analyticsService
